import { useCallback, useEffect, useState } from "react";
import { Alert, Box } from "@mui/material";
import ButtonOne from "../../common/normal-button";
import { INSTAGRAM_ACCOUNTS, INSTAGRAM_ACCOUNT_TEST } from "../../config/urls";
import InstagramConnectWizard from "./InstagramConnectWizard";
import { errorMessage, useInstagramApi } from "./shared";

// Spec 10.8 / 11: the same account and plan banners on every Instagram screen. Pass `data` (the accounts
// list response) and `onChange` when the page already loads it; otherwise the banners load it themselves.

const InstagramBanners = ({ data: given, onChange, sx }) => {
  const { request, notify, toast } = useInstagramApi();
  const [fetched, setFetched] = useState(null);
  const [reconnect, setReconnect] = useState(null);
  const [busy, setBusy] = useState(false);
  const data = given || fetched;

  const load = useCallback(() => request("get", INSTAGRAM_ACCOUNTS).then(setFetched).catch(() => {}), [request]);

  useEffect(() => {
    if (!given) load();
  }, [given, load]);

  const refresh = () => (onChange ? onChange() : load());

  const checkAgain = async (account) => {
    setBusy(true);
    try {
      await request("post", INSTAGRAM_ACCOUNT_TEST(account.id));
      notify("Checked.");
      refresh();
    } catch (error) {
      notify(errorMessage(error, "Instagram request failed."), "error");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const isAdmin = data.role === "admin";
  const dms = data.usage?.automated_dms || {};
  const dmPct = dms.limit ? (dms.used * 100) / dms.limit : 0;

  const banners = data.accounts.map((a) => {
    if (a.status === "NEEDS_REAUTH")
      return (
        <Alert key={a.id} severity="error"
          action={isAdmin && <ButtonOne variant="outlined" title="Reconnect" onClick={() => setReconnect(a)} />}>
          Instagram access for @{a.username} expired or was revoked. Reconnect to resume chats and automations.
          {!isAdmin && " Ask a workspace admin to reconnect it."}
        </Alert>
      );
    if (a.status === "MESSAGING_DISABLED")
      return (
        <Alert key={a.id} severity="warning"
          action={<ButtonOne variant="outlined" title="Check again" disabled={busy} onClick={() => checkAgain(a)} />}>
          Messaging is off for @{a.username}. In the Instagram app: 1) Settings and privacy › Messages and story
          replies, 2) Message controls, 3) under Connected tools turn on <b>Allow access to messages</b>. Then click
          Check again.
        </Alert>
      );
    return null;
  }).filter(Boolean);

  if (dmPct >= 100)
    banners.push(
      <Alert key="dms" severity="error">
        Automations are paused: all {dms.limit} automated DMs for this month are used. Agent replies still work.
        Upgrade your plan to resume automations.
      </Alert>,
    );
  else if (dmPct >= 80)
    banners.push(
      <Alert key="dms" severity="warning">
        {dms.used} of {dms.limit} automated DMs used this month. Automations pause at {dms.limit}; upgrade your plan
        to avoid the pause.
      </Alert>,
    );

  return (
    <>
      {banners.length > 0 && <Box display="flex" flexDirection="column" gap={1.5} mb={2} sx={sx}>{banners}</Box>}
      <InstagramConnectWizard
        open={Boolean(reconnect)}
        reconnect={reconnect}
        envTokenAvailable={data.env_token_available}
        request={request}
        onClose={(changed) => {
          setReconnect(null);
          if (changed) refresh();
        }}
      />
      {toast}
    </>
  );
};

export default InstagramBanners;
